import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Trash2, Copy, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown, Search, X,
  Tv, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';

const initialFormData = {
  empresa_id: '', site_id: '', brand_id: '', asset_tag: '', serial_number: '',
  modelo: '', pulgadas: '', ip_address: '', ubicacion: '', status_id: '',
  location_id: '', supplier_id: '', purchase_date: '', comments: ''
};

const Televisores = () => {
  const navigate = useNavigate();
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('micro', 'tvs');
  
  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, sin_estado: 0, top_brand: 'N/A', top_brand_count: 0 });
  
  // Data state
  const [tvs, setTvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', location_filter: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'asset_tag', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  
  // Filter options (only values that exist in tvs table)
  const [filterOptions, setFilterOptions] = useState({ brands: [], statuses: [], sites: [] });
  
  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(tvs);
  
  // Modals
  const [editModal, setEditModal] = useState({ open: false, tv: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, tv: null });
  const [formData, setFormData] = useState(initialFormData);
  const [formOptions, setFormOptions] = useState({ brands: [], statuses: [], sites: [], empresas: [], ubicaciones: [], proveedores: [], clientes: [] });
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(25);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/tvs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTvs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.asset_tag_filter) params.append('asset_tag_filter', filters.asset_tag_filter);
      if (filters.modelo_filter) params.append('modelo_filter', filters.modelo_filter);
      if (filters.ip_filter) params.append('ip_filter', filters.ip_filter);
      if (filters.location_filter) params.append('location_filter', filters.location_filter);

      const response = await api.get(`/assets/tvs?${params}`);
      setTvs(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar televisores');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/tvs/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/tvs/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
    fetchFormOptions();
  }, []);

  // Fetch on page or sort change (immediate)
  useEffect(() => {
    fetchTvs();
  }, [page, pageSize, sortConfig]);

  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchTvs();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', location_filter: '' });
    setActiveStatFilter('all');
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    if (filterType === 'all') {
      setFilters({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', location_filter: '' });
    } else if (filterType === 'active') {
      setFilters(prev => ({ ...prev, status_id: '6' }));
    } else if (filterType === 'inactive') {
      setFilters(prev => ({ ...prev, status_id: '7' }));
    } else if (filterType === 'sin_estado') {
      setFilters(prev => ({ ...prev, status_id: 'null' }));
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    setFormData(initialFormData);
    setEditModal({ open: true, tv: null, isCopy: false });
  };

  const openEditModal = (tv) => {
    setFormData({
      empresa_id: tv.empresa_id?.toString() || '',
      site_id: tv.site_id?.toString() || '',
      brand_id: tv.brand_id?.toString() || '',
      asset_tag: tv.asset_tag || '',
      serial_number: tv.serial_number || '',
      modelo: tv.modelo || '',
      pulgadas: tv.pulgadas?.toString() || '',
      ip_address: tv.ip_address || '',
      ubicacion: tv.ubicacion || '',
      status_id: tv.status_id?.toString() || '',
      location_id: tv.location_id?.toString() || '',
      supplier_id: tv.supplier_id?.toString() || '',
      cliente_id: tv.cliente_id?.toString() || '',
      purchase_date: tv.purchase_date || '',
      comments: tv.comments || ''
    });
    setEditModal({ open: true, tv, isCopy: false });
  };

  const openCopyModal = (tv) => {
    setFormData({
      empresa_id: tv.empresa_id?.toString() || '',
      site_id: tv.site_id?.toString() || '',
      brand_id: tv.brand_id?.toString() || '',
      asset_tag: '', // Vacío - campo único
      serial_number: '', // Vacío - campo único
      modelo: tv.modelo || '',
      pulgadas: tv.pulgadas?.toString() || '',
      ip_address: '', // Vacío - campo único
      ubicacion: tv.ubicacion || '',
      status_id: tv.status_id?.toString() || '',
      location_id: tv.location_id?.toString() || '',
      supplier_id: tv.supplier_id?.toString() || '',
      purchase_date: tv.purchase_date || '',
      comments: tv.comments || ''
    });
    setEditModal({ open: true, tv: null, isCopy: true });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        pulgadas: formData.pulgadas ? parseInt(formData.pulgadas) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null
      };

      if (editModal.tv && !editModal.isCopy) {
        await api.put(`/assets/tvs/${editModal.tv.id}`, payload);
        toast.success('Televisor actualizado exitosamente');
      } else {
        await api.post('/assets/tvs/', payload);
        toast.success(editModal.isCopy ? 'Televisor copiado exitosamente' : 'Televisor creado exitosamente');
      }
      
      setEditModal({ open: false, tv: null, isCopy: false });
      fetchTvs();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/tvs/${deleteModal.tv.id}`);
      toast.success('Televisor eliminado exitosamente');
      setDeleteModal({ open: false, tv: null });
      fetchTvs();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (statusName) => {
    if (!statusName) return <span className="badge badge-warning">Sin Estado</span>;
    const lower = statusName.toLowerCase();
    if (lower.includes('activo') || lower.includes('active')) {
      return <span className="badge badge-success">{statusName}</span>;
    }
    if (lower.includes('inactivo') || lower.includes('inactive') || lower.includes('baja')) {
      return <span className="badge badge-danger">{statusName}</span>;
    }
    return <span className="badge badge-default">{statusName}</span>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Televisores</h1>
            <p className="text-muted-foreground">Gestión de televisores y pantallas</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/tvs"
              filters={{ search: filters.search }}
              filename="televisores"
            />
            {canCreate && (
              <Button onClick={openCreateModal} data-testid="new-tv-btn">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nuevo Televisor
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Horizontal Compacto */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="televisores-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Tv className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('active')}
            data-testid="stats-activos"
          >
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.active}</div>
              <div className="stats-card-label">Activos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${activeStatFilter === 'inactive' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatFilter('inactive')}
            data-testid="stats-inactivos"
          >
            <XCircle className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.inactive}</div>
              <div className="stats-card-label">Inactivos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${activeStatFilter === 'sin_estado' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatFilter('sin_estado')}
            data-testid="stats-sin-estado"
          >
            <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.sin_estado}</div>
              <div className="stats-card-label">Sin Estado</div>
            </div>
          </div>
        </div>

        {/* Global Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-10 py-3 px-4">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isSomeSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Seleccionar todos"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => handleSort('asset_tag')}>
                      <div className="flex items-center gap-1">ASSET TAG <SortIcon field="asset_tag" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('brand_name')}>
                      <div className="flex items-center gap-1">MARCA <SortIcon field="brand_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => handleSort('modelo')}>
                      <div className="flex items-center gap-1">MODELO <SortIcon field="modelo" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('cliente_name')}>
                      <div className="flex items-center gap-1">CLIENTE <SortIcon field="cliente_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('ip_address')}>
                      <div className="flex items-center gap-1">IP <SortIcon field="ip_address" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('status_name')}>
                      <div className="flex items-center gap-1">ESTADO <SortIcon field="status_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('site_name')}>
                      <div className="flex items-center gap-1">SITE <SortIcon field="site_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('location_name')}>
                      <div className="flex items-center gap-1">UBICACIÓN <SortIcon field="location_name" /></div>
                    </th>
                    <th className="w-28 text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">ACCIONES</th>
                  </tr>
                  {/* Filters Row */}
                  <tr className="border-b border-border bg-muted/30">
                    <th className="w-10 py-2 px-4"></th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.asset_tag_filter} onChange={(e) => handleFilterChange('asset_tag_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.brand_id || undefined} onValueChange={(v) => handleFilterChange('brand_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.brands.map(b => (<SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.modelo_filter} onChange={(e) => handleFilterChange('modelo_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.cliente_id || undefined} onValueChange={(v) => handleFilterChange('cliente_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {formOptions.clientes?.map(c => (<SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.ip_filter} onChange={(e) => handleFilterChange('ip_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.status_id || undefined} onValueChange={(v) => handleFilterChange('status_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.statuses.map(s => (<SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.site_id || undefined} onValueChange={(v) => handleFilterChange('site_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.sites.map(s => (<SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.location_filter} onChange={(e) => handleFilterChange('location_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-8 text-muted-foreground">
                        Cargando...
                      </td>
                    </tr>
                  ) : tvs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-8">
                        <Tv className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                        <p className="text-muted-foreground">No se encontraron televisores</p>
                      </td>
                    </tr>
                  ) : (
                    tvs.map((tv) => (
                      <tr key={tv.id} className={`border-b border-border hover:bg-muted/50 transition-colors ${isSelected(tv.id) ? 'bg-primary/5' : ''}`} data-testid={`tv-row-${tv.id}`}>
                        <td className="w-10 py-3 px-4">
                          <Checkbox
                            checked={isSelected(tv.id)}
                            onCheckedChange={() => toggleRow(tv.id)}
                            aria-label={`Seleccionar ${tv.asset_tag}`}
                          />
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{tv.asset_tag || '-'}</td>
                        <td className="py-3 px-4">{tv.brand_name || '-'}</td>
                        <td className="py-3 px-4">{tv.modelo || '-'}</td>
                        <td className="py-3 px-4">{tv.cliente_name || '-'}</td>
                        <td className="py-3 px-4 font-mono text-sm">{tv.ip_address || '-'}</td>
                        <td className="py-3 px-4">{getStatusBadge(tv.status_name)}</td>
                        <td className="py-3 px-4">{tv.site_name || '-'}</td>
                        <td className="py-3 px-4">{tv.location_name || '-'}</td>
                        <td className="w-28 py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/micro/tvs/${tv.id}`)} title="Ver detalle">
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                            {canUpdate && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-primary" onClick={() => openEditModal(tv)} title="Editar">
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canCreate && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground" onClick={() => openCopyModal(tv)} title="Copiar">
                                <Copy className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => setDeleteModal({ open: true, tv })} title="Eliminar">
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} de {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Mostrar:</span>
                    <Select value={pageSize.toString()} onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}>
                      <SelectTrigger className="h-8 w-20"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">Página {page} de {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, tv: null, isCopy: false })}>
          <DialogContent className="max-w-2xl" aria-describedby="tv-form-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editModal.isCopy ? <Copy className="h-5 w-5" strokeWidth={1.5} /> : editModal.tv ? <Edit className="h-5 w-5" strokeWidth={1.5} /> : <Plus className="h-5 w-5" strokeWidth={1.5} />}
                {editModal.isCopy ? 'Copiar Televisor' : editModal.tv ? 'Editar Televisor' : 'Nuevo Televisor'}
              </DialogTitle>
            </DialogHeader>
            <p id="tv-form-description" className="text-sm text-muted-foreground">
              {editModal.isCopy ? 'Complete los campos únicos para crear una copia.' : 'Complete los datos del televisor.'}
            </p>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Asset Tag *</Label>
                <Input value={formData.asset_tag} onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))} placeholder="TV-001" />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={formData.serial_number} onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={formData.brand_id} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.brands.map(b => (
                      <SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input value={formData.modelo} onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Pulgadas</Label>
                <Input type="number" value={formData.pulgadas} onChange={(e) => setFormData(prev => ({ ...prev, pulgadas: e.target.value }))} placeholder="55" />
              </div>
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input value={formData.ip_address} onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))} placeholder="192.168.1.100" />
              </div>
              <div className="space-y-2">
                <Label>Empresa *</Label>
                <Select value={formData.empresa_id} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Site *</Label>
                <Select value={formData.site_id} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.sites.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación (FK)</Label>
                <Select value={formData.location_id} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones.map(u => (
                      <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación (texto)</Label>
                <Input value={formData.ubicacion} onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))} placeholder="Sala de reuniones" />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={formData.status_id} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={formData.supplier_id} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores.map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={formData.cliente_id} onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.clientes?.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input type="date" value={formData.purchase_date} onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Comentarios</Label>
                <Textarea value={formData.comments} onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))} rows={2} />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, tv: null, isCopy: false })}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, tv: null })}>
          <DialogContent aria-describedby="delete-tv-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Televisor
              </DialogTitle>
            </DialogHeader>
            <p id="delete-tv-description" className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar el televisor <strong>{deleteModal.tv?.asset_tag}</strong>?
              <br />
              <span className="text-sm">Esta acción no se puede deshacer.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, tv: null })}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchTvs(); fetchStats(); }}
          endpoint="/assets/tvs"
          options={{
            sites: formOptions.sites,
            empresas: formOptions.empresas,
            statuses: formOptions.statuses,
            ubicaciones: formOptions.ubicaciones
          }}
          fields={['site_id', 'empresa_id', 'status_id', 'location_id']}
        />
      </div>
    </Layout>
  );
};

export default Televisores;
